import { lazy, Suspense } from 'react';

import { Spinner } from '@apps/web/components/ui/spinner';
import { type V3ChatActions } from '@apps/web/features/phoebe_chat_v3/model/actions';
import { type V3PresentationContext } from '@apps/web/features/phoebe_chat_v3/model/presentation_context';
import {
    type V3CommsApprovalGroupEntry,
    type V3ToolEntry,
} from '@apps/web/features/phoebe_chat_v3/model/transcript';

const LazyRichPresentation = lazy(async () => {
    const module =
        await import('@apps/web/features/phoebe_chat_v3/ui/rich_presentation');
    return { default: module.V3RichPresentation };
});

const LazyCommsCard = lazy(async () => {
    const module =
        await import('@apps/web/features/phoebe_chat_v3/ui/comms_card');
    return { default: module.V3CommsCard };
});

const LazyCommsApprovalGroup = lazy(async () => {
    const module =
        await import('@apps/web/features/phoebe_chat_v3/ui/comms_approval_group');
    return { default: module.V3CommsApprovalGroup };
});

const LazyOutreachPresentation = lazy(async () => {
    const module =
        await import('@apps/web/features/phoebe_chat_v3/ui/outreach_presentation');
    return { default: module.V3OutreachPresentation };
});

export function V3CommsApprovalGroupUi({
    group,
    actions,
}: {
    readonly group: V3CommsApprovalGroupEntry;
    readonly actions: V3ChatActions;
}) {
    return (
        <Suspense fallback={<Spinner size="sm" />}>
            <LazyCommsApprovalGroup group={group} actions={actions} />
        </Suspense>
    );
}

export function V3CommsUi({
    entry,
    actions,
}: {
    readonly entry: V3ToolEntry;
    readonly actions: V3ChatActions;
}) {
    const presentation = entry.commsPresentation;
    if (presentation === null) {
        return null;
    }
    return (
        <Suspense fallback={<Spinner size="sm" />}>
            <LazyCommsCard
                entry={entry}
                presentation={presentation}
                actions={actions}
            />
        </Suspense>
    );
}

export function V3DynamicUi({
    entry,
    transcriptIndex,
    context,
    actions,
}: {
    readonly entry: V3ToolEntry;
    readonly transcriptIndex: number;
    readonly context: V3PresentationContext;
    readonly actions: V3ChatActions;
}) {
    if (entry.outreachPresentation !== null) {
        return (
            <Suspense fallback={<Spinner size="sm" />}>
                <LazyOutreachPresentation
                    presentation={entry.outreachPresentation}
                    context={context}
                />
            </Suspense>
        );
    }
    const presentation = entry.dataPresentation;
    if (presentation === null) {
        return null;
    }
    switch (presentation.presentation) {
        case 'shifts':
        case 'caregivers':
        case 'client_detail':
        case 'table':
            return (
                <Suspense fallback={<Spinner size="sm" />}>
                    <LazyRichPresentation
                        entry={entry}
                        transcriptIndex={transcriptIndex}
                        context={context}
                        actions={actions}
                    />
                </Suspense>
            );
        default: {
            const exhaustivePresentation: never = presentation.presentation;
            return exhaustivePresentation;
        }
    }
}
